<template>
  <Teleport to="body" :disabled="!appendToBody">
    <transition name="ds-dialog-fade">
      <div
        v-if="modelValue || !destroyOnClose"
        v-show="modelValue"
        class="ds-dialog__overlay"
        @click="onOverlayClick"
      >
        <div
          ref="dialogRef"
          v-bind="$attrs"
          class="ds-dialog"
          :class="customClass"
          :style="dialogStyle"
          role="dialog"
          aria-modal="true"
          @click.stop
        >
          <header class="ds-dialog__header" :style="headerStyle">
            <div class="ds-dialog__title">
              <slot name="header">{{ title }}</slot>
            </div>
            <button
              v-if="showClose"
              class="ds-dialog__close"
              type="button"
              aria-label="Cerrar"
              @click="requestClose"
            >
              ×
            </button>
          </header>

          <section class="ds-dialog__body">
            <slot />
          </section>

          <footer v-if="$slots.footer" class="ds-dialog__footer">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
export default {
  inheritAttrs: false,
  name: "DsDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: "" },
    width: { type: String, default: "90%" },
    height: { type: String, default: "90vh" },
    maxWidth: { type: String, default: "90%" },
    maxHeight: { type: String, default: "90vh" },
    customClass: { type: String, default: "" },
    showClose: { type: Boolean, default: true },
    closeOnClickModal: { type: Boolean, default: true },
    closeOnPressEscape: { type: Boolean, default: true },
    destroyOnClose: { type: Boolean, default: false },
    appendToBody: { type: Boolean, default: true },
    beforeClose: { type: Function, default: null },
    headerBgColor: { type: String, default: "var(--main-color)" },
  },
  emits: ["update:modelValue", "open", "close"],
  computed: {
    dialogStyle() {
      return {
        width: this.width,
        maxWidth: this.maxWidth,
        height: this.height,
        maxHeight: this.maxHeight,
      };
    },
    headerStyle() {
      return {
        backgroundColor: this.headerBgColor,
      };
    },
  },
  watch: {
    modelValue(value) {
      if (value) {
        this.$emit("open");
      }
      this.lockScroll(value);
    },
  },
  mounted() {
    if (this.modelValue) {
      this.lockScroll(true);
    }
    document.addEventListener("keydown", this.onKeyDown);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeyDown);
    this.lockScroll(false);
  },
  methods: {
    onOverlayClick() {
      if (this.closeOnClickModal) {
        this.requestClose();
      }
    },
    onKeyDown(event) {
      if (!this.modelValue) {
        return;
      }
      if (this.closeOnPressEscape && event.key === "Escape") {
        this.requestClose();
      }
    },
    requestClose() {
      if (!this.beforeClose) {
        this.closeDialog();
        return;
      }

      try {
        if (this.beforeClose.length > 0) {
          this.beforeClose(() => this.closeDialog());
          return;
        }

        const result = this.beforeClose();
        if (result !== false) {
          this.closeDialog();
        }
      } catch (error) {
        // Fallback safe close when beforeClose fails unexpectedly.
        this.closeDialog();
      }
    },
    closeDialog() {
      this.$emit("update:modelValue", false);
      this.$emit("close");
    },
    lockScroll(shouldLock) {
      const body = document.body;
      if (!body) {
        return;
      }

      if (shouldLock) {
        body.style.overflow = "hidden";
      } else {
        body.style.overflow = "";
      }
    },
  },
};
</script>

<style scoped>
.ds-dialog__overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.ds-dialog {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ds-dialog__header {
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin: 0;
}

.ds-dialog__title {
  flex: 1;
  min-width: 0;
}

.ds-dialog__close {
  border: none;
  background: transparent;
  color: #ffffff;
  font-size: 28px;
  line-height: 1;
  padding: 8px 14px;
  cursor: pointer;
}

.ds-dialog__close:hover {
  opacity: 0.8;
}

.ds-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 20px;
}

.ds-dialog__footer {
  flex-shrink: 0;
  padding: 10px 20px 20px;
}

.ds-dialog-fade-enter-active,
.ds-dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.ds-dialog-fade-enter-from,
.ds-dialog-fade-leave-to {
  opacity: 0;
}
</style>
